import { createStyles, makeStyles, Theme } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import {
  BOX_SHADOWS,
  DATA_TYPES,
  FONTS,
  SECONDARY_COLOR,
  SORT_TYPES,
} from "../../constants";
import {
  addAlpha,
  calcPercentile,
  convertToNumber,
} from "../../helpers/DataHelpers";
import { formatNumber } from "../../helpers/FormatNumber";
import Dataset from "../../MachineLearning/Dataset";
import { stateType, stateTypeF } from "../../state";
import { BoxHeader } from "../Common/BoxHeader";
import { makeHistData } from "../Common/makeHistData";
import { FilterButton } from "./FilterButton";
import { applyFiltersToData } from "./filters";
import styles from "./scroll.module.css";

const CELL_HEIGHT = "30px";
const ROWS_PER_PAGE = 50;

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      backgroundColor: "white",
      boxShadow: BOX_SHADOWS,
      borderRadius: "10px",
      height: "500px",
      transition: "0.5s",
      overflow: "hidden",
    },
    scrollContainer: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "white",
      transition: "0.5s",
      width: "100%",
    },
    line: {
      width: "100%",
      height: "1px",
      backgroundColor: "rgba(0,0,0,0.2)",
      position: "relative",
      marginTop: "-10px",
    },
    row: {
      display: "flex",
      flexDirection: "row",
      margin: "10px",
      marginLeft: "0px",
      marginTop: "0px",
    },
    rowChecked: {
      display: "flex",
      flexDirection: "row",
      margin: "10px",
      marginLeft: "0px",
      paddingLeft: "0px",
      marginTop: "0px",
    },
    rowWithLine: {
      display: "flex",
      flexDirection: "column",
      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
    },
    rowWithLineChecked: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: addAlpha(SECONDARY_COLOR, 0.2),
    },
    header: {
      display: "flex",
      flexDirection: "row",
      margin: "10px",
      marginLeft: "0px",
      cursor: "pointer",
    },
    cell: {
      fontFamily: FONTS,
      fontSize: "14px",
      lineHeight: "30px",
    },
    headerCell: {
      fontFamily: FONTS,
      fontSize: "14px",
      "&:hover": { color: "rgba(0,0,0,.6)" },
    },
    sortArrow: {
      height: "17px",
      width: "17px",
      marginTop: "2px",
      transition: "0.3s",
    },
    footerContainer: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "right",
      marginTop: "10px",
      marginBottom: "10px",
    },
    pageArrowLeft: {
      width: "17px",
      height: "17px",
      marginTop: "2px",
      transform: "rotate(180deg)",
      "&:hover": { transform: "scale(1.2) rotate(180deg)" },
    },
    pageArrowRight: {
      width: "17px",
      height: "17px",
      marginTop: "2px",

      "&:hover": { transform: "scale(1.2)" },
    },
    pageArrowContainer: {
      width: "30px",
      height: "30px",
      cursor: "pointer",
    },
    headerContainer: { display: "flex" },
    filterButtonContainer: {
      marginTop: "25px",
      marginRight: "40px",
      marginLeft: "auto",
      cursor: "pointer",
    },
    filterButton: { "&:hover": { transform: "scale(1.05)" } },
    dropDown: {
      width: "140px",
      height: "30px",
      border: "solid",
      marginLeft: "20px",
      borderRadius: "5px",
      borderColor: "rgba(0,0,0,0.2)",
      borderWidth: "2px",
      marginTop: "-5px",
      display: "flex",
      flexDirection: "row",
      transition: "0.3s",
      cursor: "pointer",
      backgroundColor: "white",
      "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
    },
  });
});

export function makeCleanTableData(dataset: Dataset) {
  let rowCount = Array(dataset.columns[0].vectorizedData.length)
    .fill(0)
    .map((_, i) => i + 1);
  let pageData: any[][] = [rowCount];
  for (let col of dataset.columns) {
    let tmp;
    try {
      tmp = dataset.data[col.columnName];
    } catch {
      let a = 1;
    }

    if (col.availableColumnTypes.includes(DATA_TYPES.number)) {
      tmp = tmp.map((val) => convertToNumber(val));
    }
    pageData.push(tmp);
  }
  pageData = pageData[0].map((_, colIndex) =>
    pageData.map((row) => row[colIndex])
  );
  return pageData;
}

export function Line() {
  return (
    <div
      style={{
        width: "100%",
        height: "1px",
        backgroundColor: "rgba(0,0,0,0.2)",
        position: "relative",
        marginTop: "-10px",
        overflow: "hidden",
      }}
    ></div>
  );
}

type tableHeaderInputs = {
  state: stateTypeF;
  headers: string[];
  colWidths: string[];
};
function TableHeader({ state, headers, colWidths }: tableHeaderInputs) {
  const classes = useStyles();
  const rowNum = -1;
  let isChecked = rowNum in state.saveState.cleanData.cleanRowsSelected;
  function handleClick() {
    let selected = state.saveState.cleanData.cleanRowsSelected;
    if (isChecked) {
      for (let row of state.cleanFilteredData) {
        if (row[0] in selected) {
          delete selected[row[0]];
        }
        delete selected[-1];
      }
    } else {
      selected[rowNum] = 0;
      state.cleanFilteredData.forEach((val) => (selected[val[0]] = 0));
    }

    let col = state.saveState.dataset.getColumn(
      state.saveState.cleanData.cleanHistSelected
    );
    let { data } = applyFiltersToData(
      state.saveState.dataset.data,
      state.saveState.cleanData.cleanHistSelected,
      state.saveState.cleanData.cleanDataFilters,
      selected,
      state.saveState.cleanData.valuesModified
    );

    let histData = makeHistData(
      data,
      col.columnType,
      selected,
      col.colStats.median
    );

    state.saveState.cleanData.cleanHistData = histData;

    state.setSaveState(state.saveState);
    state.saveState.cleanData.cleanRowsSelected = selected;
    state.setSaveState(state.saveState);
  }

  return (
    <div
      className={classes.rowWithLine}
      style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
    >
      <div className={classes.header}>
        <div
          className={classes.cell}
          style={{
            height: "40px",
            marginTop: "-10px",
            marginBottom: "-5px",
          }}
        >
          <Checkbox checked={isChecked} onClick={handleClick}></Checkbox>
        </div>

        {headers.map((colName, i) => (
          <TableHeadCell
            colName={colName}
            width={colWidths[i]}
            height={CELL_HEIGHT}
            index={i}
            state={state}
          ></TableHeadCell>
        ))}
      </div>
      <Line></Line>
    </div>
  );
}

type tableRowType = { state: stateTypeF; row: any[]; colWidths: string[] };
export function TableRow({ state, row, colWidths }: tableRowType) {
  const classes = useStyles();
  const rowNum = row[0];
  let isChecked = rowNum in state.saveState.cleanData.cleanRowsSelected;
  function handleClick() {
    let selected = state.saveState.cleanData.cleanRowsSelected;
    if (isChecked) {
      delete selected[rowNum];
    } else {
      selected[rowNum] = 0;
    }
    state.saveState.cleanData.cleanRowsSelected = selected;
    state.setSaveState(state.saveState);
    let col = state.saveState.dataset.getColumn(
      state.saveState.cleanData.cleanHistSelected
    );
    let { data } = applyFiltersToData(
      state.saveState.dataset.data,
      state.saveState.cleanData.cleanHistSelected,
      state.saveState.cleanData.cleanDataFilters,
      selected,
      {}
    );

    let histData = makeHistData(
      data,
      col.columnType,
      selected,
      col.colStats.median
    );
    state.saveState.cleanData.cleanHistData = histData;
    state.setSaveState(state.saveState);
  }

  let rowClass = isChecked ? classes.rowWithLineChecked : classes.rowWithLine;

  return (
    <div className={rowClass} onClick={handleClick}>
      <div className={classes.row}>
        <div
          className={classes.cell}
          style={{
            height: "40px",
            marginTop: "-5px",
            marginBottom: "-5px",
          }}
        >
          <Checkbox checked={isChecked}></Checkbox>
        </div>
        {row.map((val, i) => (
          <TableCell
            value={val}
            width={colWidths[i]}
            height={CELL_HEIGHT}
            index={i}
          ></TableCell>
        ))}
      </div>
      <Line></Line>
    </div>
  );
}

type tableCellType = {
  value: string | number;
  width: string;
  height: string;
  index: number;
  state?: stateTypeF;
};

type tableHeadCellType = {
  colName: string;
  width: string;
  height: string;
  index: number;
  state: stateTypeF;
};

function TableCell({ value, width, height, index }: tableCellType) {
  const classes = useStyles();
  const isFirst = index === 0;
  const isLast = index === 11;
  return (
    <div
      className={classes.cell}
      style={{
        height: height,
        width: width,
        marginLeft: index === 0 ? "0px" : "20px",
      }}
    >
      <div
        style={{
          textAlign: isFirst ? "left" : "right",
          marginLeft: isFirst ? "10px" : "0px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function TableHeadCell({
  colName,
  width,
  height,
  index,
  state,
}: tableHeadCellType) {
  const classes = useStyles();
  const isFirst = index === 0;
  const isSelected = state.cleanTableColSort[0] === colName;

  function handleClick(colName: string) {
    function sortFunctionASC(a, b) {
      if (a[index] === b[index]) {
        return 0;
      } else {
        return a[index] > b[index] ? -1 : 1;
      }
    }
    function sortFunctionDESC(a, b) {
      if (a[index] === b[index]) {
        return 0;
      } else {
        return a[index] < b[index] ? -1 : 1;
      }
    }

    let isSameCol = state.cleanTableColSort[0] === colName;
    let sortDir = SORT_TYPES.asc;
    if (isSameCol) {
      if (state.cleanTableColSort[1] === SORT_TYPES.asc) {
        sortDir = SORT_TYPES.desc;
      }
    }

    const sortFunction =
      sortDir === SORT_TYPES.asc ? sortFunctionASC : sortFunctionDESC;
    let data = state.cleanFilteredData;
    data.sort(sortFunction);
    state.setCleanFilteredData(data);

    state.setCleanTableColSort([colName, sortDir]);
  }

  return (
    <div
      className={classes.headerCell}
      style={{
        height: height,
        width: width,
        marginLeft: index === 0 ? "0px" : "20px",
      }}
      onClick={() => handleClick(colName)}
    >
      <div
        style={{
          justifyContent: isFirst ? "left" : "right",
          display: "flex",
        }}
      >
        {isFirst ? (
          <div></div>
        ) : (
          <div
            style={{
              height: "0px",
            }}
          >
            {isSelected ? (
              <div
                style={{
                  width: "0px",
                  marginRight: "10px",
                  marginLeft: "-10px",
                }}
              >
                <ArrowDownwardIcon
                  className={classes.sortArrow}
                  style={{
                    transform:
                      state.cleanTableColSort[1] === SORT_TYPES.asc
                        ? "rotate(-180deg)"
                        : "rotate(0deg)",
                  }}
                ></ArrowDownwardIcon>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        )}
        <div
          style={{
            fontWeight: "bold",
            marginLeft: isFirst ? "10px" : "10px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {colName}
        </div>
        {!isFirst ? (
          <div></div>
        ) : (
          <div style={{ height: "0px" }}>
            {isSelected ? (
              <div
                style={{
                  width: "0px",
                  marginLeft: "10px",
                  marginRight: "-10px",
                }}
              >
                <ArrowDownwardIcon
                  className={classes.sortArrow}
                  style={{
                    transform:
                      state.cleanTableColSort[1] === SORT_TYPES.asc
                        ? "rotate(-180deg)"
                        : "rotate(0deg)",
                  }}
                ></ArrowDownwardIcon>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function calcColumnWidths(header: string, data: any[]) {
  const headerWidth = header.length;
  let subData = data.slice(0, 1000);
  let lengths = [];
  for (let val of subData) {
    try {
      lengths.push(val.toString().length);
    } catch {
      lengths.push(0);
    }
  }

  let dataWidth = calcPercentile(lengths, 0.9, DATA_TYPES.number);

  let colWidths = Math.max(headerWidth, dataWidth);
  colWidths = Math.max(Math.min(colWidths * 9 + 20, 250), 80);

  return colWidths + "px";
}

function ScrollTable({ state }: stateType) {
  const classes = useStyles();
  let headers = state.cleanData.headers;

  let page = state.cleanTablePage;
  let subData = state.cleanFilteredData;

  const colWidths: string[] = [];
  for (let i = 0; i < headers.length; i++) {
    let row = subData.map((row) => row[i]);
    colWidths.push(calcColumnWidths(headers[i], row));
  }

  let pageData = subData.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE
  );

  return (
    <div className={styles.scrollContainer}>
      <div style={{ width: "fit-content", minWidth: "100%" }}>
        <TableHeader state={state} headers={headers} colWidths={colWidths} />
      </div>
      <div
        style={{ width: "fit-content", minWidth: "100%" }}
        className={styles.scrollContainer}
      >
        {pageData.map((row) => (
          <TableRow state={state} row={row} colWidths={colWidths}></TableRow>
        ))}
      </div>
    </div>
  );
}

function Footer({ state }: stateType) {
  const classes = useStyles();
  let page = state.cleanTablePage;
  let nRows = formatNumber(state.cleanFilteredData.length);
  let start = "0";
  let end = "0";
  if (nRows !== "0") {
    start = formatNumber(page * ROWS_PER_PAGE + 1);
    end = formatNumber(
      Math.min(
        (page + 1) * ROWS_PER_PAGE,
        convertToNumber(nRows.replace(",", ""))
      )
    );
    let numPages = Math.ceil(
      convertToNumber(nRows.replace(",", "")) / ROWS_PER_PAGE
    );
    if (page > numPages - 1) {
      state.setCleanTablePage(numPages - 1);
    }
  }

  function rightArrowClick() {
    let nPages = Math.ceil(
      convertToNumber(nRows.replace(",", "")) / ROWS_PER_PAGE
    );
    if (page < nPages - 1) {
      state.setCleanTablePage(page + 1);
    }
  }

  function leftArrowClick() {
    if (page > 0) {
      state.setCleanTablePage(page - 1);
    }
  }

  let nSelected = Object.keys(
    state.saveState.cleanData.cleanRowsSelected
  ).length;
  if (-1 in state.saveState.cleanData.cleanRowsSelected) {
    nSelected -= 1;
  }

  return (
    <div className={classes.footerContainer}>
      <div
        style={{
          marginLeft: "20px",
          marginRight: "auto",
          fontFamily: FONTS,
        }}
      >
        Selected to Remove: {formatNumber(nSelected)}
      </div>
      <div style={{ marginRight: "20px", fontFamily: FONTS }}>
        {" "}
        {start}-{end} of {nRows}
      </div>
      <div className={classes.pageArrowContainer} onClick={leftArrowClick}>
        <ArrowForwardIosIcon className={classes.pageArrowLeft} />
      </div>
      <div
        className={classes.pageArrowContainer}
        style={{ marginRight: "20px" }}
        onClick={rightArrowClick}
      >
        <ArrowForwardIosIcon className={classes.pageArrowRight} />
      </div>
    </div>
  );
}

export function RowSelectTable({ state }: stateType) {
  const classes = useStyles();

  if (state.cleanData === undefined) {
    return <div></div>;
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <BoxHeader
          title={"Rows Excluded from Analysis/Training"}
          toolTip={"Select any rows you want to exclude from your dataset."}
          line={false}
        ></BoxHeader>
        <FilterButton state={state}></FilterButton>
      </div>
      <ScrollTable state={state}></ScrollTable>
      <Footer state={state}></Footer>
    </div>
  );
}
